import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * `<ThirdPartyConsentView />` — SHT-mediated third-party-app
 * consent flow (ADR-027 §1 steps 2-6 PHG-side).
 *
 * Drives the `IConsentFlow` orchestrator + dispatches step views by
 * current state. Composes:
 *
 *   IThirdPartyAppRegistryClient — verified app metadata
 *   IConsentArtifactClient        — Patient-Directed Use Authority
 *                                   Consent Artifact issuance
 *
 * **Doctrinal posture (load-bearing):**
 *   - Hand-off / hand-back is the architectural seam. External
 *     apps NEVER render this consent UI in their own surface;
 *     decertification trigger per ADR-027 §3. Step 2 (hand-off TO
 *     PHG) is the flow's initial state; step 6 hand-back URL
 *     surfaces in the terminal granted/denied step view via
 *     `getHandbackUrl()`.
 *   - No silent / dismissal grant per ADR-027 §1 step 4. Confirmation
 *     step requires explicit grant or deny click; cancel is a
 *     separate no-action exit (does NOT issue a Consent Artifact).
 *   - Verified registered identity replaces app-claimed identity at
 *     `app-metadata-loaded` per ADR-027 §1 step 3.
 *   - Tier-aware rendering per BMA v4.0 §2.2.4.
 *
 * **Doctrinal-copy gate.** Patient-Facing Terms v1.3 (coord issue
 * #42) supplies canonical consent language when v1.3 lands;
 * placeholder copy aligned with ADR-027 + BMA §2.2.4 + Tech Spec
 * §6.1.2 + PFO §3 until then.
 */
import * as React from 'react';
import { useLocale } from '../i18n/locale-context.js';
import { ConsentLoadingStep } from './_consent/loading-step.js';
import { AppIdentityStep } from './_consent/app-identity-step.js';
import { ScopeReviewStep } from './_consent/scope-review-step.js';
import { RetentionReviewStep } from './_consent/retention-review-step.js';
import { TierDisclosureStep } from './_consent/tier-disclosure-step.js';
import { ConsentConfirmationStep } from './_consent/confirmation-step.js';
import { ConsentIssuingStep } from './_consent/issuing-step.js';
import { HandbackStep } from './_consent/handback-step.js';
import { ConsentCancelledOrErrorStep } from './_consent/cancelled-or-error-step.js';
function useConsentFlowState(flow) {
    const [state, setState] = React.useState(() => flow.getState());
    React.useEffect(() => {
        return flow.subscribe(setState);
    }, [flow]);
    return state;
}
export function ThirdPartyConsentView(props) {
    const state = useConsentFlowState(props.flow);
    const { t } = useLocale();
    // Eager metadata load on mount when arriving at the loading state.
    React.useEffect(() => {
        if (state.kind === 'loading-app-metadata') {
            void props.flow.loadAppMetadata();
        }
        // Only run on mount + when flow identity changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.flow]);
    return (_jsxs("section", { "aria-labelledby": "consent-heading", children: [_jsx("h2", { id: "consent-heading", children: t('consent.heading') }), renderStep(state, props)] }));
}
function renderStep(state, props) {
    const { flow } = props;
    const handleClose = () => {
        if (props.onClose !== undefined) {
            props.onClose();
        }
    };
    switch (state.kind) {
        case 'loading-app-metadata':
            return _jsx(ConsentLoadingStep, {});
        case 'app-identity-review':
            return (_jsx(AppIdentityStep, { app: state.app, onContinue: () => flow.continueFromAppIdentity(), onCancel: () => flow.cancel('user-cancelled-at-app-identity') }));
        case 'scope-review':
            return (_jsx(ScopeReviewStep, { requestedDataScope: state.initiation.requestedDataScope, requestedOperationalScope: state.initiation.requestedOperationalScope, currentDataScope: state.narrowing.dataScope, currentOperationalScope: state.narrowing.operationalScope, onNarrow: (opts) => flow.narrowScope(opts), onContinue: () => flow.continueFromScopeReview(), onCancel: () => flow.cancel('user-cancelled-at-scope') }));
        case 'retention-review':
            return (_jsx(RetentionReviewStep, { requestedRetentionDiscipline: state.initiation.requestedRetentionDiscipline, requestedTimeBounds: state.initiation.requestedTimeBounds, currentRetentionDiscipline: state.narrowing.retentionDiscipline, currentTimeBounds: state.narrowing.timeBounds, onNarrow: (opts) => flow.narrowRetention(opts), onContinue: () => flow.continueFromRetentionReview(), onCancel: () => flow.cancel('user-cancelled-at-retention') }));
        case 'tier-disclosure':
            return (_jsx(TierDisclosureStep, { app: state.app, onContinue: () => flow.continueFromTierDisclosure(), onCancel: () => flow.cancel('user-cancelled-at-tier-disclosure') }));
        case 'confirmation':
            return (_jsx(ConsentConfirmationStep, { app: state.app, initiation: state.initiation, narrowing: state.narrowing, onGrant: () => {
                    void flow.grant();
                }, onDeny: () => flow.deny(), onCancel: () => flow.cancel('user-cancelled-at-confirmation') }));
        case 'issuing-artifact':
            return _jsx(ConsentIssuingStep, {});
        case 'granted': {
            const handbackUrl = flow.getHandbackUrl() ?? state.initiation.handbackUrl;
            return (_jsx(HandbackStep, { outcome: "granted", app: state.app, handbackUrl: handbackUrl, artifact: state.artifact }));
        }
        case 'denied': {
            const handbackUrl = flow.getHandbackUrl() ?? state.initiation.handbackUrl;
            return _jsx(HandbackStep, { outcome: "denied", app: state.app, handbackUrl: handbackUrl });
        }
        case 'cancelled':
            return (_jsx(ConsentCancelledOrErrorStep, { kind: "cancelled", detail: state.reason, onClose: handleClose }));
        case 'error':
            return (_jsx(ConsentCancelledOrErrorStep, { kind: "error", detail: `${state.code}: ${state.message}`, onClose: handleClose }));
    }
}
//# sourceMappingURL=third-party-consent-view.js.map